import { CommonModule } from '@angular/common'
import { Component, DestroyRef, inject, Input, OnInit, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { MatIconModule } from '@angular/material/icon'
import { BillingService, roundMoney } from '@app/modules/payments/services/billing.service'
import { ToastrService } from '@app/services/toastr.service'
import { BillingPaymentMethod } from '@app/v2/settings/components/billing-payments/billing/payment-cards-modal/payment-cards-modal.component'
import { WalletTopUpModalComponent } from '@app/v2/settings/components/billing-payments/billing/wallet-top-up-modal/wallet-top-up-modal.component'
import { OrganizationService } from '@app/v2/settings/services/organization.service'
import { NavbarService } from '@app/v2/shared/components/navbar/navbar.service'
import { listenLayeredOverlayDismiss } from '@app/v2/shared/helpers/layered-overlay-dismiss'
import { AuthService } from '@auth/services/auth.service'
import { NotificationChannelPickerComponent } from '@database/components/notification-channel-picker/notification-channel-picker.component'
import { Registration } from '@database/models/registration.model'
import {
  NotificationChannel,
  isEmailOnlyNotificationChannel,
  notificationChannelDisplayName,
  notificationChannelsForForm,
  resendConfirmationChannelsForBulk,
  resendConfirmationChannelsForSingle,
  resendConfirmationSuccessToastBody
} from '@database/models/resend-confirmation-pricing'
import { DatabaseService, ResendConfirmationSingleChannel } from '@database/services/database.service'
import { NbButtonModule, NbCardModule, NbDialogRef, NbIconModule } from '@nebular/theme'
import { catchError, of, take } from 'rxjs'

export type ResendConfirmationFlowStep = 'channel' | 'cost' | 'topup'

@Component({
  selector: 'ngx-resend-confirmation-flow',
  templateUrl: './resend-confirmation-flow.component.html',
  standalone: true,
  imports: [CommonModule, NbCardModule, NbButtonModule, NbIconModule, MatIconModule, NotificationChannelPickerComponent, WalletTopUpModalComponent]
})
export class ResendConfirmationFlowComponent implements OnInit {
  @Input() mode: 'bulk' | 'single' = 'bulk'

  /** Number of recipients (bulk count or 1 for single). */
  @Input() attendeeCount = 0

  @Input() eventSlug = ''

  /** Bulk-only */
  @Input() isChildSelected = false
  @Input() bulkAction = ''
  @Input() bulkPersonaSlug: string | null = null
  @Input() bulkIds: string[] = []
  @Input() bulkCount = 0
  @Input() parentId?: string
  @Input() search?: string

  /** Single-only */
  @Input() registrationId = ''
  @Input() registration?: Registration

  step: ResendConfirmationFlowStep = 'channel'
  selectedChannel: NotificationChannel | null = null

  channels: NotificationChannel[] = []

  sending = false
  loadingBalance = false
  loadingPaymentMethods = false

  /** Shown inline on the cost step when wallet balance is too low. */
  insufficientBalance = false
  /** True after the initial balance check on the cost step completes. */
  balanceChecked = false
  requiredAmountUsd = 0
  currentBalanceUsd = 0

  public readonly paymentMethods = signal<BillingPaymentMethod[]>([])

  /** Template + headerTitle — shared with notification-channel-picker / invite. */
  readonly channelDisplayName = notificationChannelDisplayName

  private readonly _destroyRef = inject(DestroyRef)
  private readonly _organizationService = inject(OrganizationService)
  private readonly _billingService = inject(BillingService)

  constructor(
    private dialogRef: NbDialogRef<ResendConfirmationFlowComponent>,
    private databaseService: DatabaseService,
    private navbarService: NavbarService,
    private authService: AuthService,
    private toastrService: ToastrService
  ) { }

  ngOnInit() {
    const allChannels = this.mode === 'single' ? resendConfirmationChannelsForSingle() : resendConfirmationChannelsForBulk()
    this.channels = notificationChannelsForForm(allChannels, this._resolveFormForChannels())
    listenLayeredOverlayDismiss({
      closeParent: () => this.close(),
      destroyRef: this._destroyRef
    })
    if (isEmailOnlyNotificationChannel(this.channels)) {
      this.selectChannel(NotificationChannel.EMAIL)
    }
  }

  /**
   * Persona whose form_elements gate SMS / WhatsApp (phone type id).
   * Single: registrant form slug. Bulk: selected persona slug (child slug when members are selected).
   * @returns Matching persona from DatabaseService.forms, if loaded
   */
  private _resolveFormForChannels() {
    const slug = this.mode === 'single' ? this.registration?.source?.form : this.bulkPersonaSlug
    if (!slug) return undefined
    return this.databaseService.forms?.value?.find((form: { slug?: string }) => form.slug === slug)
  }

  close() {
    this.dialogRef.close()
  }

  /**
   * Primary cost-step CTA label (wallet check, top-up, send).
   * @returns {string} Copy for the current button state
   */
  confirmSendButtonLabel(): string {
    let state: 'loading' | 'topUp' | 'sending' | 'checking' | 'retry' | 'send' = 'send'
    if (this.insufficientBalance && this.loadingPaymentMethods) state = 'loading'
    else if (this.insufficientBalance) state = 'topUp'
    else if (this.sending) state = 'sending'
    else if (this.loadingBalance) state = 'checking'
    else if (!this.balanceChecked) state = 'retry'

    switch (state) {
      case 'loading':
        return 'Loading…'
      case 'topUp':
        return 'Top up'
      case 'sending':
        return 'Sending…'
      case 'checking':
        return 'Checking…'
      case 'retry':
        return 'Retry'
      case 'send':
        return 'Yes, send'
    }
  }

  get headerTitle(): string {
    if (this.step === 'channel') {
      return 'Select confirmation channel'
    }
    if (this.step === 'topup') {
      return 'Top up wallet'
    }
    if (this.step === 'cost' && this.selectedChannel !== null) {
      return `Resend confirmations via ${notificationChannelDisplayName(this.selectedChannel)}`
    }
    return ''
  }

  costPerLabel(ch: NotificationChannel): string {
    switch (ch) {
      case NotificationChannel.EMAIL:
        return 'Cost per email'
      case NotificationChannel.SMS:
        return 'Cost per SMS'
      case NotificationChannel.WHATSAPP:
        return 'Cost per message'
    }
  }

  /** Intro line on the cost step — wording differs per channel (matches product copy). */
  resendConfirmationIntroMessage(ch: NotificationChannel, count: number): string {
    const attendeeWord = count === 1 ? 'attendee' : 'attendees'
    switch (ch) {
      case NotificationChannel.SMS:
        return `You are about to resend confirmation SMS to ${count} ${attendeeWord}.`
      case NotificationChannel.WHATSAPP:
        return `You are about to resend confirmation messages to ${count} ${attendeeWord}.`
      case NotificationChannel.EMAIL:
        return `You are about to resend confirmation emails to ${count} ${attendeeWord}.`
    }
  }

  selectChannel(ch: NotificationChannel) {
    this.selectedChannel = ch
    this.enterCostStep()
  }

  /**
   * Return to the channel picker, or close when Email was auto-selected (no picker to return to).
   */
  backToChannels() {
    if (isEmailOnlyNotificationChannel(this.channels)) {
      this.close()
      return
    }
    this.insufficientBalance = false
    this.balanceChecked = false
    this.loadingBalance = false
    this.step = 'channel'
  }

  /**
   * Enter the cost step and always re-check wallet balance against the resend total.
   */
  enterCostStep(): void {
    this.step = 'cost'
    this.checkWalletBalance()
  }

  unitPrice(ch: NotificationChannel): number {
    return this.authService.resendConfirmationUnitPriceUsd[ch]
  }

  totalPrice(): number {
    if (this.selectedChannel === null) return 0
    return roundMoney(this.authService.calculateConfirmationTotalPriceUsd(this.selectedChannel, this.attendeeCount))
  }

  /**
   * Amount still needed in the wallet to cover the resend total.
   */
  balanceShortfallUsd(): number {
    return Math.max(0, roundMoney(this.requiredAmountUsd - this.currentBalanceUsd))
  }

  /**
   * Suggested top-up for the wallet: cost minus balance, floored at $50.
   */
  suggestedTopUpAmount(): number {
    const shortfall = roundMoney(this.requiredAmountUsd - this.currentBalanceUsd)
    return Math.max(50, shortfall)
  }

  /**
   * Fetch wallet balance and compare it to the resend total when entering the cost step.
   * @param {number} [optimisticBalanceUsd] Known balance to apply immediately (e.g. after top-up).
   */
  checkWalletBalance(optimisticBalanceUsd?: number): void {
    if (this.selectedChannel === null) return
    if (!this._billingService.workspaceId) {
      this.loadingBalance = false
      this.balanceChecked = false
      this.toastrService.error('Could not verify wallet balance. Please try again.', '')
      return
    }

    if (optimisticBalanceUsd !== undefined) {
      this._applyBalanceCheck(optimisticBalanceUsd)
    } else {
      this.loadingBalance = true
      this.insufficientBalance = false
      this.balanceChecked = false
    }

    const workspaceId = String(this._billingService.workspaceId)
    this.navbarService
      .getBalance(workspaceId)
      .pipe(take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (balance) => {
          const resolvedBalance = optimisticBalanceUsd !== undefined ? Math.max(balance, optimisticBalanceUsd) : balance
          this._applyBalanceCheck(resolvedBalance)
        },
        error: () => {
          this.loadingBalance = false
          this.balanceChecked = false
          this.toastrService.error('Could not verify wallet balance. Please try again.', '')
        }
      })
  }

  /**
   * Update balance state and whether the wallet can cover the resend cost.
   * @param {number} balance Current wallet balance in USD
   */
  private _applyBalanceCheck(balance: number): void {
    this.loadingBalance = false
    this.balanceChecked = true
    this.currentBalanceUsd = roundMoney(balance)
    const total = roundMoney(this.totalPrice())
    this.requiredAmountUsd = total
    const shortfall = roundMoney(total - this.currentBalanceUsd)
    this.insufficientBalance = shortfall > 0
  }

  /**
   * Send confirmation, open top-up when balance is insufficient, or retry balance check after an error.
   */
  onConfirmSend() {
    if (this.selectedChannel === null || !this.authService.currentWorkspace?.id) return

    if (this.insufficientBalance) {
      this.goToTopUpStep()
      return
    }

    if (this.loadingBalance) return

    if (!this.balanceChecked) {
      this.checkWalletBalance()
      return
    }

    this.executeSend()
  }

  /**
   * Load payment methods and navigate to the inline top-up step.
   */
  goToTopUpStep(): void {
    if (this.loadingPaymentMethods) return

    this.loadingPaymentMethods = true
    this._organizationService
      .getOrganizationPaymentMethods(this._billingService.workspaceId)
      .pipe(
        catchError(() => of([] as BillingPaymentMethod[])),
        take(1),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((methods) => {
        this.paymentMethods.set(methods)
        this.loadingPaymentMethods = false
        this.step = 'topup'
      })
  }

  /**
   * Return from top-up to the cost step and re-check wallet balance.
   */
  backFromTopUp(): void {
    this.enterCostStep()
  }

  /**
   * After a successful top-up, return to the cost step and re-check wallet balance.
   */
  public onWalletTopUpSuccess(amount: number): void {
    if (!this._billingService.workspaceId) return

    const updatedBalance = roundMoney(this.currentBalanceUsd + amount)
    this._billingService.publishWalletBalance(updatedBalance)
    this.step = 'cost'
    this.checkWalletBalance(updatedBalance)
  }

  private executeSend() {
    if (this.selectedChannel === null) return
    this.sending = true

    if (this.mode === 'single') {
      const ch = this.selectedChannel as ResendConfirmationSingleChannel
      const registration = this.registration
      const waContext = ch === NotificationChannel.WHATSAPP && registration ? { formSlug: registration.source.form } : undefined
      this.databaseService
        .resendConfirmation(this.eventSlug, this.registrationId, ch, waContext)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: () => {
            this.sending = false
            this.toastrService.success('Success', resendConfirmationSuccessToastBody(ch))
            if (registration) {
              if (ch === NotificationChannel.EMAIL && registration.source.mail_status) {
                registration.source.mail_status.pending_mail_count++
                registration.calculateMailStatus(true)
              }
              setTimeout(() => registration.updateSelf(), 10000)
            }
            this.dialogRef.close(true)
          },
          error: () => {
            this.sending = false
            this.toastrService.error('Something went wrong', 'Please try again later')
          }
        })
      return
    }

    this.databaseService
      .bulkResendConfirmation(this.eventSlug, this.bulkAction, this.bulkPersonaSlug ?? '', this.bulkIds, this.bulkCount, this.parentId, this.search, this.selectedChannel)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this.sending = false
          this.dialogRef.close(true)
        },
        error: (err) => {
          this.sending = false
          console.error(err)
          this.toastrService.error('Something went wrong', 'Please try again later')
        }
      })
  }
}
